import React, { useEffect, useState } from 'react';

interface PaginationJumpProps {
  totalPages?: number;
  currentPage?: number;
  onJump: (page: number) => void;
  label?: string;
}

const PaginationJump: React.FC<PaginationJumpProps> = ({
  totalPages = 1,
  currentPage = 1,
  onJump,
  label = 'Skip to page',
}) => {
  const [ pageInput, setPageInput ] = useState('');

  useEffect(() => {
    setPageInput('');
  }, [ currentPage, totalPages ]);

  if (!totalPages || totalPages <= 1) {
    return null;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/[^0-9]/g, '');
    setPageInput(value);
  };

  const handleSubmit = () => {
    if (!pageInput) {
      return;
    }

    const parsed = Number(pageInput);
    if (!Number.isFinite(parsed)) {
      return;
    }

    const maxPage = Math.max(1, totalPages);
    const clamped = Math.max(1, Math.min(parsed, maxPage));
    if (clamped === currentPage) {
      setPageInput('');
      return;
    }

    onJump(clamped);
    setPageInput('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  const isOnLastPage = currentPage >= totalPages;
  const handleJumpToLast = () => {
    if (isOnLastPage) {
      return;
    }
    onJump(totalPages);
    setPageInput('');
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        gap: '4px',
        minWidth: '180px',
        alignSelf: 'flex-end',
      }}
    >
      <span
        style={{
          color: 'var(--Text-Subtle, var(--card-text))',
          fontSize: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}
      >
        {label}
      </span>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <input
          type='number'
          min={1}
          max={totalPages}
          value={pageInput}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={`${currentPage}/${totalPages}`}
          style={{
            width: '82px',
            padding: '6px 10px',
            borderRadius: '8px',
            border: '1px solid #2E3036',
            background: 'var(--Background-200, var(--bg-colors))',
            color: 'var(--text-primary)',
            fontSize: '14px',
          }}
        />
        <button
          type='button'
          onClick={handleSubmit}
          disabled={!pageInput}
          style={{
            padding: '6px 14px',
            borderRadius: '8px',
            border: 'none',
            background: 'var(--Primary-400, #4895EF)',
            color: '#fff',
            fontWeight: 600,
            cursor: pageInput ? 'pointer' : 'not-allowed',
            opacity: pageInput ? 1 : 0.4,
            transition: 'opacity 0.2s ease-in-out',
          }}
        >
          Go
        </button>
        <button
          type='button'
          onClick={handleJumpToLast}
          disabled={isOnLastPage}
          title='Go to last page'
          style={{
            padding: '6px 14px',
            borderRadius: '8px',
            border: '1px solid #2E3036',
            background: 'transparent',
            color: 'var(--text-primary)',
            fontWeight: 600,
            cursor: isOnLastPage ? 'not-allowed' : 'pointer',
            opacity: isOnLastPage ? 0.4 : 1,
            transition: 'opacity 0.2s ease-in-out',
          }}
        >
          Last »
        </button>
      </div>
    </div>
  );
};

export default PaginationJump;
